import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import AdminLayout from '../layouts/AdminLayout';
import StatCard from '../components/StatCard';
import { getTotalHadiths } from '../services/hadithService';
import { getTotalDuas } from '../services/duaService';
import { getTotalQuotes } from '../services/quoteService';
import { getTotalUsers, getTodayActiveUsers } from '../services/userService';
import { getTotalBanners } from '../services/bannerService';
import { getTotalProphets } from '../services/prophetService';
import { getTotalAdhkar } from '../services/tasbeehService';
import { getTotalSurahs } from '../services/quranService';
import {
  Grid, Typography, Box, makeStyles, CircularProgress
} from '@material-ui/core';
import PageAnalyticsSection from '../components/PageAnalyticsSection';
import UserGrowthSection from '../components/UserGrowthSection';
import FirebaseUsageSection from '../components/FirebaseUsageSection';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import FavoriteIcon from '@material-ui/icons/Favorite';
import FormatQuoteIcon from '@material-ui/icons/FormatQuote';
import PeopleIcon from '@material-ui/icons/People';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import ViewModuleIcon from '@material-ui/icons/ViewModule';
import PersonIcon from '@material-ui/icons/Person';
import ExposureIcon from '@material-ui/icons/Exposure';
import ViewCarouselIcon from '@material-ui/icons/ViewCarousel';

const useStyles = makeStyles((theme) => ({
  statsRow: {
    marginBottom: theme.spacing(4) },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: theme.spacing(3) },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif' },
  actionCard: {
    height: '100%',
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    cursor: 'pointer',
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 20,
    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
    '&:hover': {
      transform: 'translateY(-4px)',
      background: theme.palette.type === "dark" ? "rgba(30,41,59,0.8)" : "rgba(255,255,255,0.8)",
      borderColor: 'rgba(16,185,129,0.3)',
      boxShadow: '0 12px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(16,185,129,0.1)',
      '& $actionIconWrapper': {
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: '#fff',
        boxShadow: '0 8px 24px rgba(16,185,129,0.4)',
        transform: 'scale(1.05)' },
      '& $actionLabel': {
        color: '#10b981' } } },
  actionIconWrapper: {
    width: 64,
    height: 64,
    borderRadius: 16,
    background: 'rgba(148,163,184,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    marginBottom: 16,
    transition: 'all 0.3s ease',
    '& svg': { fontSize: 28 } },
  actionLabel: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '0.9rem',
    transition: 'color 0.3s ease' },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400 },
  loadingText: {
    marginTop: 16,
    color: theme.palette.text.secondary,
    fontWeight: 500 } }));

export default function DashboardPage() {
  const classes = useStyles();
  const history = useHistory();
  const [stats, setStats] = useState({ hadith: 0, duas: 0, quotes: 0, users: 0, activeToday: 0, surahs: 0, prophets: 0, adhkar: 0, banners: 0 });
  const [loading, setLoading] = useState(true);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const [hadith, duas, quotes, users, activeToday, surahs, prophets, adhkar, banners] = await Promise.all([
        getTotalHadiths(),
        getTotalDuas(),
        getTotalQuotes(),
        getTotalUsers(),
        getTodayActiveUsers(),
        getTotalSurahs(),
        getTotalProphets(),
        getTotalAdhkar(),
        getTotalBanners(),
      ]);
      setStats({ hadith, duas, quotes, users, activeToday, surahs, prophets, adhkar, banners });
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const quickActions = [
    { label: 'Add Hadith', icon: <MenuBookIcon />, path: '/hadith/create' },
    { label: 'Add Dua', icon: <FavoriteIcon />, path: '/duas/create' },
    { label: 'Add Quote', icon: <FormatQuoteIcon />, path: '/quotes/create' },
    { label: 'Add Prophet', icon: <PersonIcon />, path: '/prophets/create' },
    { label: 'Add Tasbeeh', icon: <ExposureIcon />, path: '/tasbeeh/create' },
    { label: 'Add Banner', icon: <ViewCarouselIcon />, path: '/banners/create' },
    { label: 'View Users', icon: <PeopleIcon />, path: '/users' },
    { label: 'System Tools', icon: <TrendingUpIcon />, path: '/maintenance' },
  ];

  if (loading) {
    return (
      <AdminLayout title="Dashboard" onRefresh={loadStats}>
        <div className={classes.loadingContainer}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <CircularProgress size={48} thickness={4} />
            <Typography className={classes.loadingText}>Syncing metrics...</Typography>
          </Box>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard Overview" onRefresh={loadStats}>
      <Grid container spacing={3} className={classes.statsRow}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<MenuBookIcon />} value={stats.hadith} label="Total Hadith" index={0} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<FavoriteIcon />} value={stats.duas} label="Total Duas" index={1} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<FormatQuoteIcon />} value={stats.quotes} label="Total Quotes" index={2} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<PeopleIcon />}
            value={stats.users}
            label="Total Users"
            index={3}
            onClick={() => history.push('/users')}
            subText="View All Users →"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={<TrendingUpIcon />}
            value={stats.activeToday}
            label="Active Today"
            index={4}
            onClick={() => history.push('/users?activity=today')}
            subText="View Daily Users →"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<ViewModuleIcon />} value={stats.surahs} label="Total Surahs" index={5} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<PersonIcon />} value={stats.prophets} label="Total Prophets" index={6} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<ExposureIcon />} value={stats.adhkar} label="Total Adhkar" index={7} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<ViewCarouselIcon />} value={stats.banners} label="Total Banners" index={8} />
        </Grid>
      </Grid>

      {/* User Growth (Left) & Firebase Data (Right) Section — Above Quick Actions */}
      <Box mt={2} mb={5}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} lg={6} style={{ display: 'flex' }}>
            <UserGrowthSection />
          </Grid>
          <Grid item xs={12} lg={6} style={{ display: 'flex' }}>
            <FirebaseUsageSection />
          </Grid>
        </Grid>
      </Box>

      <Box className={classes.sectionHeader} mt={6}>
        <Typography className={classes.sectionTitle}>Quick Actions</Typography>
      </Box>
      
      <Grid container spacing={3}>
        {quickActions.map((action, i) => (
          <Grid item xs={6} sm={4} md={2} key={action.path}>
            <div className={classes.actionCard} onClick={() => history.push(action.path)}>
              <div className={classes.actionIconWrapper}>
                {action.icon}
              </div>
              <Typography className={classes.actionLabel}>
                {action.label}
              </Typography>
            </div>
          </Grid>
        ))}
      </Grid>

      {/* Screen & Page View Analytics */}
      <PageAnalyticsSection />
    </AdminLayout>
  );
}